import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCreateDeviceVariant, useUpdateDeviceVariant } from '@/hooks/useCatalogActions';
import type { DeviceVariant } from '@octave/contracts';
import { createLogger } from '@/lib/logger';

const log = createLogger('DeviceVariantForm');
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const deviceVariantSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  condition: z.enum(['NEW', 'USED']),
  price: z.number().min(1, 'Le prix doit être supérieur à 0'),
  weight: z.number().min(1, 'Le poids doit être supérieur à 0'),
  color: z.string().optional(),
  capacity: z
    .number()
    .min(1, 'La capacité doit être supérieure à 0')
    .optional(),
  isActive: z.boolean().default(true),
});

type DeviceVariantFormData = z.infer<typeof deviceVariantSchema>;

interface DeviceVariantFormProps {
  deviceModelId: string;
  variant?: DeviceVariant | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const DEVICE_CONDITIONS = [
  { value: 'NEW', label: 'Neuf' },
  { value: 'USED', label: 'Reconditionné' },
];

const COMMON_COLORS = [
  // Couleurs de base
  'Noir',
  'Blanc',
  'Bleu',
  'Rouge',
  'Vert',
  'Gris',
  'Or',
  'Argent',
  'Rose',
  'Violet',
  'Minuit',

  // Couleurs Apple
  'Bleu Apple',
  'Mauve',
  'Lumière stellaire',
  'Product Red',
  'Violet intense',
  'Noir sidéral',
  'Titane noir',
  'Titane blanc',
  'Titane bleu',
  'Titane naturel',
  'Jaune Apple',
  'Vert Apple',

  // Couleurs Samsung
  'Noir Onyx',
  'Gris Marbre',
  'Violet Cobalt',
  'Jaune Ambre',
  'Vert Jade',
  'Bleu Saphir',
  'Orange Grès',
  'Titane gris',
  'Titane violet',
  'Titane jaune',
  'Vert Titane',
  'Bleu Titane',
  'Orange Titane',
  'Silver Shadow',
  'Mint',
  'Ice Blue',
  'Navy',
  'Titane Silver Blue',
  'Titane White Silver',
];

const COMMON_CAPACITIES = [32, 64, 128, 256, 512, 1024, 2048];

// Fonction pour afficher la capacité de manière lisible
const formatCapacity = (capacity: number): string => {
  if (capacity >= 1024) {
    const tb = capacity / 1024;
    return `${tb} To`;
  }
  return `${capacity} Go`;
};

export function DeviceVariantForm({
  deviceModelId,
  variant,
  onSuccess,
  onCancel,
}: DeviceVariantFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createDeviceVariant = useCreateDeviceVariant();
  const updateDeviceVariant = useUpdateDeviceVariant();

  const form = useForm<DeviceVariantFormData>({
    resolver: zodResolver(deviceVariantSchema),
    defaultValues: {
      name: variant?.name || '',
      condition:
        variant?.condition === 'NEW' || variant?.condition === 'USED'
          ? variant.condition
          : undefined,
      price: variant?.price ? variant.price / 100 : 0,
      weight: variant?.weight || 0,
      color: variant?.color || '',
      capacity: variant?.capacity || undefined,
      isActive: variant?.isActive ?? true,
    },
  });

  const onSubmit = async (data: DeviceVariantFormData) => {
    setIsSubmitting(true);
    try {
      // Convert euros to centimes for backend
      const dataForBackend = {
        ...data,
        price: Math.round(data.price * 100),
      };

      if (variant) {
        await updateDeviceVariant.mutateAsync({
          id: variant.id,
          data: dataForBackend,
        });
      } else {
        await createDeviceVariant.mutateAsync({
          deviceModelId,
          ...dataForBackend,
        });
      }
      onSuccess();
    } catch (error) {
      log.error('Form submission error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom de la déclinaison</FormLabel>
              <FormControl>
                <Input placeholder="128GB Noir, 256GB Blanc, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="condition"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Condition</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une condition" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {DEVICE_CONDITIONS.map((condition) => (
                      <SelectItem key={condition.value} value={condition.value}>
                        {condition.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>À l'acte (en euros)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="1"
                    placeholder="999.00"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="weight"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Poids (en grammes)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="200"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="capacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capacité (en GB)</FormLabel>
                <Select
                  onValueChange={(value) =>
                    field.onChange(
                      value === 'none' ? undefined : parseInt(value)
                    )
                  }
                  value={field.value?.toString() || 'none'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une capacité" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {COMMON_CAPACITIES.map((capacity) => (
                      <SelectItem key={capacity} value={capacity.toString()}>
                        {formatCapacity(capacity)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Couleur</FormLabel>
                <Select
                  onValueChange={(value) =>
                    field.onChange(value === 'none' ? undefined : value)
                  }
                  value={field.value || 'none'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une couleur" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {COMMON_COLORS.map((color) => (
                      <SelectItem key={color} value={color}>
                        {color}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {field.value ? 'Actif' : 'Inactif'}
                </FormLabel>
                <div className="text-sm text-muted-foreground">
                  {field.value
                    ? 'Cette déclinaison sera visible dans le catalogue'
                    : 'Cette déclinaison ne sera pas visible dans le catalogue'}
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              createDeviceVariant.isPending ||
              updateDeviceVariant.isPending
            }
          >
            {isSubmitting ||
            createDeviceVariant.isPending ||
            updateDeviceVariant.isPending
              ? 'Enregistrement...'
              : variant
              ? 'Mettre à jour'
              : 'Créer'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
